import React, { useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

interface Props {
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  color?: string;
  deltaPct?: number;
  onClick?: () => void;
  clickable?: boolean;
  details?: {
    type: 'disbursements' | 'repayments' | 'applications' | 'approved' | 'rejected' | 'overdue';
    data?: any[];
    summary?: any;
  };
}

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  type: string;
  data?: any[];
  summary?: any;
}

const DetailModal: React.FC<DetailModalProps> = ({ isOpen, onClose, title, type, data, summary }) => {
  if (!isOpen) return null;

  const renderContent = () => {
    switch (type) {
      case 'disbursements':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Disbursement Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalDisbursed ? `${Number(summary.totalDisbursed).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Average Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.avgDisbursed ? `${Number(summary.avgDisbursed).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Loans</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalLoans || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Recent Disbursements</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName || `Loan ${item.id}`}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.amount ? `${Number(item.amount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'repayments':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Repayment Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Repaid</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalRepaid ? `${Number(summary.totalRepaid).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Repayment Rate</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.repaymentRate ? `${Math.round(summary.repaymentRate * 100)}%` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>On-Time Payments</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.onTimePayments || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Recent Repayments</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName || `Payment ${item.id}`}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.amount ? `${Number(item.amount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'applications':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Applications Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Applications</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalApplications || 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Pending</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.pendingApplications || 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Average Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.avgApplicationAmount ? `${Number(summary.avgApplicationAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Recent Applications</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {item.submissionDate ? new Date(item.submissionDate).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.requestedAmount ? `${Number(item.requestedAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'approved':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Approved Loans Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Approved</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.approvedLoans || 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalApprovedAmount ? `${Number(summary.totalApprovedAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Approval Rate</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.approvalRate ? `${Math.round(summary.approvalRate * 100)}%` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Approved Applications</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Approved: {item.approvalDate ? new Date(item.approvalDate).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.requestedAmount ? `${Number(item.requestedAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'rejected':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Rejected Applications Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Rejected</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.rejectedLoans || 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Rejection Rate</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.rejectionRate ? `${Math.round(summary.rejectionRate * 100)}%` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Top Reason</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.topRejectionReason || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Rejected Applications</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Reason: {item.rejectionReason || 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.requestedAmount ? `${Number(item.requestedAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'overdue':
        return (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Overdue Loans Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>30+ Days Past Due</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.overdue30Days || 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Total Overdue Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>
                    {summary?.totalOverdueAmount ? `${Number(summary.totalOverdueAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Risk Level</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>
                    {summary?.riskLevel || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {data && data.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#1e293b' }}>Overdue Loans</h4>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {data.slice(0, 10).map((item, index) => (
                    <div key={index} style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.applicantName}</div>
                        <div style={{ fontSize: 12, color: '#ef4444' }}>
                          {item.daysOverdue || 0} days overdue
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>
                        {item.overdueAmount ? `${Number(item.overdueAmount).toLocaleString('en-ZW')} ZiG` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return <div>No details available</div>;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 24,
        maxWidth: 800,
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, color: '#1e293b' }}>{title} Details</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#64748b',
              padding: 8,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

const KpiCard: React.FC<Props> = ({ title, value, subtitle, color = '#1976d2', deltaPct, onClick, clickable = false, details }) => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useLanguage();

  const handleClick = () => {
    if (clickable && details) {
      setShowModal(true);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <div 
        style={{
          width: '100%',
          minHeight: 120,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          padding: 16,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          transform: 'scale(1)'
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (clickable) {
            e.currentTarget.style.transform = 'scale(1.01)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06)';
          }
        }}
        onMouseLeave={(e) => {
          if (clickable) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)';
          }
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: '12px 12px 0 0'
        }} />
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: color
          }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1.1, wordBreak: 'break-word' }}>{value}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto', paddingTop: 8 }}>
          {typeof deltaPct === 'number' && (
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              color: deltaPct >= 0 ? '#10b981' : '#ef4444',
              padding: '1px 4px',
              borderRadius: 4,
              background: deltaPct >= 0 ? '#ecfdf5' : '#fef2f2'
            }}>
              {deltaPct >= 0 ? '↗' : '↘'} {Math.abs(Math.round(deltaPct * 100)) / 100}%
            </span>
          )}
          {subtitle && <div style={{ fontSize: 10, color: '#94a3b8' }}>{subtitle}</div>}
          {clickable && (
            <div style={{ 
              fontSize: 9, 
              color: '#64748b', 
              marginLeft: 'auto',
              padding: '1px 4px',
              borderRadius: 3,
              background: '#f1f5f9',
              fontWeight: 500
            }}>
              {t('kpi.clickForDetails')}
            </div>
          )}
        </div>
      </div>
      
      {details && (
        <DetailModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={title}
          type={details.type}
          data={details.data}
          summary={details.summary}
        />
      )}
    </>
  );
};

export default KpiCard;

